import React, { FC, useState, useEffect, ChangeEvent, useCallback, useMemo } from 'react';
import { InputAdornment, TextField } from '@mui/material';
import { Search, HighlightOff } from '@mui/icons-material';
import { debounce } from 'lodash';
import WFDatePicker from 'components/DatePicker/DatePicker';
import MenuWithCheckboxes, { CheckboxOption } from 'components/Menu/MenuWithCheckboxes';

import { ICampaignDashboardFilters } from '../CampaignDashboard';
import { TCampaignDisplayType } from 'reduxState/store/campaigns/types';

const statusOptions: CheckboxOption[] = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
  { label: 'Expired', value: 'EXPIRED' },
  { label: 'Disabled', value: 'DISABLED' },
];

interface ICampaignDashboardFiltersProps {
  filters: ICampaignDashboardFilters;
  setFilters: (filters: ICampaignDashboardFilters) => void;
  displayTypeOptions: CheckboxOption[];
}

const CampaignDashboardFilters: FC<React.PropsWithChildren<ICampaignDashboardFiltersProps>> = ({
  filters,
  setFilters,
  displayTypeOptions,
}) => {
  const [inputValue, setInputValue] = useState(filters.searchTerm);

  useEffect(() => {
    setInputValue(filters.searchTerm);
  }, [filters.searchTerm]);

  const handleStatusSelect = (option: CheckboxOption) => {
    const newSelectedStatus = filters.status.includes(option.value)
      ? filters.status.filter(v => v !== option.value)
      : [...filters.status, option.value];

    setFilters({ ...filters, status: newSelectedStatus });
  };

  const handleOnDateChange = (date: Date | null, type: 'startDate' | 'endDate') => {
    setFilters({ ...filters, [type]: date });
  };

  const handleOnDateClear = (type: 'startDate' | 'endDate') => {
    setFilters({ ...filters, [type]: null });
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFilters({ ...filters, searchTerm: value });
      }, 500),
    [filters, setFilters],
  );

  const onSearchHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      debouncedSearch(event.target.value);
    },
    [debouncedSearch],
  );

  const onClickClear = (): void => {
    setInputValue('');
    setFilters({ ...filters, searchTerm: '' });
  };

  const handleDisplayTypeSelect = (option: CheckboxOption) => {
    const currentDisplayTypes = filters.displayType;
    const newSelectedDisplayTypes = currentDisplayTypes.includes(option.value as TCampaignDisplayType)
      ? currentDisplayTypes.filter(v => v !== option.value)
      : [...currentDisplayTypes, option.value as TCampaignDisplayType];

    setFilters({ ...filters, displayType: newSelectedDisplayTypes });
  };

  return (
    <div className="flex flex-wrap mb-[30px] justify-between items-center w-full min-w-0 max-w-full max-[1279px]:flex-col">
      <div className="flex flex-row flex-wrap gap-1 max-xl:flex-1 max-[1279px]:w-full min-w-0">
        <WFDatePicker
          label="Start Date"
          date={filters.startDate}
          onDateChange={(date: Date | null) => {
            handleOnDateChange(date, 'startDate');
          }}
          onDateClear={() => {
            handleOnDateClear('startDate');
          }}
        />
        <WFDatePicker
          label="End Date"
          date={filters.endDate}
          onDateChange={(date: Date | null) => {
            handleOnDateChange(date, 'endDate');
          }}
          onDateClear={() => {
            handleOnDateClear('endDate');
          }}
        />
      </div>
      <div className="flex flex-wrap gap-1 items-center max-xl:flex-1 max-[1279px]:w-full min-w-0">
        <TextField
          className="h-[40px] max-w-[225px] min-w-0 rounded-[6px] bg-white max-sm:max-w-none max-sm:w-full max-sm:min-w-0 border-0 shadow-md max-xl:w-full max-xl:min-w-0 max-xl:max-w-full flex-grow-2 max-lg:my-1.5 min-[1250px]:ml-auto"
          placeholder="Search campaigns"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <div className="flex [&>*:hover]:cursor-pointer">
                  {inputValue ? (
                    <HighlightOff onClick={onClickClear} className="cursor-pointer search-bar-icon" />
                  ) : (
                    <Search />
                  )}
                </div>
              </InputAdornment>
            ),
          }}
          inputProps={{
            className: 'h-[40px] py-2.5 px-3.5 box-border font-montserrat text-base',
          }}
          sx={{
            fieldset: { border: '0' },
          }}
          onChange={(event): void => {
            event.persist();
            setInputValue(event.target.value);
            onSearchHandler(event);
          }}
          value={inputValue}
        />

        <div className="flex max-lg:flex-1 gap-0.5">
          <MenuWithCheckboxes
            label="Status"
            options={statusOptions}
            selectedOptions={filters.status}
            onSelect={handleStatusSelect}
            className="flex justify-between my-1.5 mx-0.5 min-w-[150px] max-w-[200px] text-[#575757] text-base hover:border-none max-sm:text-sm max-sm:whitespace-nowrap max-lg:w-full max-lg:max-w-none max-lg:min-w-0 max-lg:mr-3 max-lg:ml-0"
          />

          <MenuWithCheckboxes
            label="Display Type"
            options={displayTypeOptions || []}
            selectedOptions={filters.displayType}
            onSelect={handleDisplayTypeSelect}
            className="flex justify-between my-1.5 mx-0.5 min-w-[150px] max-w-[200px] text-[#575757] text-base hover:border-none max-sm:text-sm max-sm:whitespace-nowrap max-lg:w-full max-lg:max-w-none max-lg:min-w-0 max-lg:mr-3 max-lg:ml-0"
          />
        </div>
      </div>
    </div>
  );
};

export default CampaignDashboardFilters;
